import "zod/compile";
import { OPTION_LEG_TYPES } from "@erp/shared-types";
import { oc } from "@orpc/contract";
import { z } from "zod";
import { getSupplyDemandLinesContract } from "./line-supply-demand.contract";

const DateSchema = z.iso.date();
const QuantitySchema = z.number().finite();
export const ReplenishmentEventTypeSchema = z.enum([
  "independent_demand",
  "dependent_demand",
  "ero",
  "aro",
  "bom_demand",
  "fo_demand",
  "ss_demand",
  "project_order",
  "rsq",
  "release",
  "on_hand",
  "unshipped",
]);
export type ReplenishmentEventType = z.infer<
  typeof ReplenishmentEventTypeSchema
>;

export const GetReplenishmentDetailInputSchema = z
  .object({
    sku: z.string().trim().min(1).max(255),
    warehouseId: z.string().uuid(),
    /** Inclusive UTC calendar dates. Omit to show today through 730 days out. */
    dateFrom: DateSchema.optional(),
    dateTo: DateSchema.optional(),
    mode: z.enum(["events", "daily", "weekly", "monthly"]).default("daily"),
    includeEmptyBuckets: z.boolean().default(false),
    /** Filters visibility only; never changes the inventory calculation. */
    eventTypes: z.array(ReplenishmentEventTypeSchema).max(12).optional(),
  })
  .refine((v) => !v.dateFrom || !v.dateTo || v.dateFrom <= v.dateTo, {
    message: "dateFrom must be on or before dateTo",
    path: ["dateTo"],
  });
export type GetReplenishmentDetailInput = z.infer<
  typeof GetReplenishmentDetailInputSchema
>;

export const ReplenishmentNavigationSchema = z.object({
  target: z.enum(["forecast_detail", "replenishment_suggestion"]),
  sku: z.string(),
  warehouseId: z.string().uuid(),
  suggestionId: z.string().uuid().nullable(),
  /** Sync Forecast Detail to the policy run that supplied this curve. */
  runId: z.string().uuid().nullable(),
});
/** The open order line behind an ERO event, for drill-down. */
export const ReplenishmentOrderReferenceSchema = z.object({
  kind: z.enum(["purchase_order", "transfer_order"]),
  /** PO or TO header id. */
  id: z.string().uuid(),
  /** Human order number (PO external id / TO transfer number), when stored. */
  number: z.string().nullable(),
});
export const ReplenishmentDetailEventSchema = z.object({
  id: z.string(),
  type: ReplenishmentEventTypeSchema,
  date: DateSchema.nullable(),
  endDate: DateSchema.nullable(),
  supplyQty: QuantitySchema,
  demandQty: QuantitySchema,
  /** Full source quantity, before clipping a monthly display allocation. */
  sourceQty: QuantitySchema.nullable(),
  source: z.string(),
  sourceDate: DateSchema.nullable(),
  allocation: z.enum(["none", "monthly_calendar_days"]),
  /** Moves projected on hand. SS demand and ARO release do not. */
  affectsBalance: z.boolean(),
  /** Date derived by this read (ARO release/arrival), not stored on the source. */
  estimated: z.boolean(),
  /** Null on non-ARO events. Uncredited AROs have no arrival date. */
  aroState: z.enum(["planned", "awaiting_ack", "unavailable"]).nullable(),
  aroUnavailableReason: z
    .enum(["missing_quantity", "missing_lead_time", "unresolved_route"])
    .nullable(),
  aroLeg: z
    .object({
      legId: z.string().uuid(),
      optionId: z.string().uuid(),
      legType: z.enum(["buy", "transfer"]),
      sourceWarehouseId: z.string().uuid().nullable(),
    })
    .nullable(),
  /** False means POH/PIPE are unknown because planned supply is incomplete. */
  supplyComplete: z.boolean(),
  /** An open order expected before today, shown on the Past Due row. */
  pastDue: z.boolean(),
  /** Projected on hand at end of endDate, with every input type included. */
  projectedOnHand: QuantitySchema.nullable(),
  /** POH, less the SS level from the lead-time end onward (Gains PIPE). */
  pipe: QuantitySchema.nullable(),
  reference: ReplenishmentOrderReferenceSchema.nullable(),
  navigation: ReplenishmentNavigationSchema.nullable(),
});
export type ReplenishmentDetailEvent = z.infer<
  typeof ReplenishmentDetailEventSchema
>;

/**
 * Per-type quantities in one bucket, Gains' grid columns. Visible-type filter
 * applies; opening-position fields are non-zero only on the as-of bucket.
 */
export const ReplenishmentBucketQuantitiesSchema = z.object({
  onHand: QuantitySchema,
  unshipped: QuantitySchema,
  openSalesOrders: QuantitySchema,
  stagedTransfers: QuantitySchema,
  independentDemand: QuantitySchema,
  eroSupply: QuantitySchema,
  aroSupply: QuantitySchema,
  aroRelease: QuantitySchema,
  ssDemand: QuantitySchema,
});
export type ReplenishmentBucketQuantities = z.infer<
  typeof ReplenishmentBucketQuantitiesSchema
>;
export const ReplenishmentDetailBucketSchema = z.object({
  date: DateSchema,
  endDate: DateSchema,
  /** The Past Due row: open orders expected before today. Sorted first. */
  pastDue: z.boolean(),
  /** onHand + eroSupply + aroSupply. */
  supplyQty: QuantitySchema,
  /** unshipped + openSalesOrders + stagedTransfers + independentDemand + ssDemand. */
  demandQty: QuantitySchema,
  quantities: ReplenishmentBucketQuantitiesSchema,
  /** Gains "SS Event": the SS level for rows up to the lead-time end, else 0. */
  ssEvent: QuantitySchema,
  /** Includes all input types regardless of the event visibility filter. */
  projectedOnHand: QuantitySchema.nullable(),
  pipe: QuantitySchema.nullable(),
  forecastCovered: z.boolean(),
  supplyComplete: z.boolean(),
  period: z.enum(["within_lead_time", "coverage_window", "beyond_coverage"]),
});
export type ReplenishmentDetailBucket = z.infer<
  typeof ReplenishmentDetailBucketSchema
>;

const LegSchema = z.object({
  id: z.string().uuid(),
  legType: z.enum(OPTION_LEG_TYPES),
  rank: z.number().int(),
  quantity: QuantitySchema.nullable(),
  sourceWarehouseId: z.string().uuid().nullable(),
  shipToWarehouseId: z.string().uuid().nullable(),
  supplierId: z.string().uuid().nullable(),
  estimatedLeadTimeDays: QuantitySchema.nullable(),
  status: z.string(),
  purchaseOrderLineItemId: z.string().uuid().nullable(),
  transferOrderLineItemId: z.string().uuid().nullable(),
});
export const ReplenishmentDetailPlanSchema = z.object({
  suggestionId: z.string().uuid(),
  generatedAt: z.string(),
  status: z.string(),
  neededQuantity: QuantitySchema,
  chosenOptionId: z.string().uuid().nullable(),
  options: z.array(
    z.object({
      id: z.string().uuid(),
      rank: z.number().int(),
      source: z.string(),
      rationale: z.string().nullable(),
      legs: z.array(LegSchema),
    }),
  ),
  navigation: ReplenishmentNavigationSchema,
});
export type ReplenishmentDetailPlan = z.infer<
  typeof ReplenishmentDetailPlanSchema
>;

/**
 * Lead time, extended to the first open order due after it whose quantity
 * alone covers the worst PIPE shortfall inside lead time. `covered` names that
 * order; `uncovered` means no single order does; `unknown` means PIPE is
 * unknown somewhere inside lead time.
 */
export const ReplenishmentCriticalPeriodSchema = z.object({
  status: z.enum(["clear", "covered", "uncovered", "unknown"]),
  leadTimeEnd: DateSchema,
  /** Last day of the critical period: lead-time end unless covered. */
  end: DateSchema,
  /** First day PIPE goes below zero inside lead time. */
  breachDate: DateSchema.nullable(),
  /** The lowest PIPE inside lead time, as a positive quantity. */
  shortfall: QuantitySchema.nullable(),
  coveringOrder: z
    .object({
      /** The ERO event this order line produced. */
      eventId: z.string(),
      kind: z.enum(["purchase_order", "transfer_order"]),
      id: z.string().uuid(),
      number: z.string().nullable(),
      expectedDate: DateSchema,
      quantity: QuantitySchema,
    })
    .nullable(),
});
export type ReplenishmentCriticalPeriod = z.infer<
  typeof ReplenishmentCriticalPeriodSchema
>;

export const GetReplenishmentDetailResponseSchema = z.object({
  sku: z.string(),
  warehouseId: z.string().uuid(),
  asOf: DateSchema,
  dateFrom: DateSchema,
  dateTo: DateSchema,
  mode: z.enum(["events", "daily", "weekly", "monthly"]),
  forecastRunId: z.string().uuid().nullable(),
  /** The forecast run's method and demand class for this SKU and location. */
  forecastModel: z
    .object({ method: z.string(), demandClass: z.string().nullable() })
    .nullable()
    .default(null),
  forecastGrain: z.literal("month").nullable(),
  forecastRange: z.object({ from: DateSchema, to: DateSchema }).nullable(),
  events: z.array(ReplenishmentDetailEventSchema),
  buckets: z.array(ReplenishmentDetailBucketSchema),
  criticalPeriod: ReplenishmentCriticalPeriodSchema,
  plans: z.array(ReplenishmentDetailPlanSchema),
  /** Unfiltered leg counts; null when all incoming supply can be scheduled. */
  incompleteSupply: z
    .object({
      awaitingAck: z.number().int().nonnegative(),
      unavailable: z.number().int().nonnegative(),
    })
    .nullable(),
  capabilities: z.array(
    z.object({
      type: ReplenishmentEventTypeSchema,
      availability: z.enum(["available", "partial", "unsupported"]),
      explanation: z.string(),
    }),
  ),
  pipeProvenance: z.object({
    formula: z.string(),
    semantics: z.literal("gains_pipe_display_allocation"),
    snapshotDate: DateSchema.nullable(),
    onHand: QuantitySchema.nullable(),
    reservedUnshipped: QuantitySchema.nullable(),
    stagedOutbound: QuantitySchema,
    openSalesOrderDemand: QuantitySchema,
    serviceStock: QuantitySchema.nullable(),
    effectiveTriggerLevel: QuantitySchema.nullable(),
    startingPosition: QuantitySchema.nullable(),
    leadTimeDays: QuantitySchema,
    leadTimeSource: z.string(),
    leadTimeSampleN: z.number().int(),
    leadTimeEnd: DateSchema,
    /** Gains PIPE at end of lead time (POH − SS level); display, not phase-1 sizing. */
    leadTimePipe: QuantitySchema.nullable(),
    /** The SS demand event date: the lead-time end. */
    ssEventDate: DateSchema,
    /** POH/PIPE are unknown from this uncertain supply release onward. */
    incompleteSupplyFrom: DateSchema.nullable(),
    coverageEnd: DateSchema,
    notes: z.array(z.string()),
  }),
  /** Current phase-1 recomputation, not the stored batch's historical decision. */
  engineProjection: z
    .object({
      coverageMonths: QuantitySchema,
      breachIndex: z.number().int().nullable(),
      projectedAtBreach: QuantitySchema,
      orderQuantity: QuantitySchema,
    })
    .nullable(),
  limitations: z.array(z.string()),
});
export type GetReplenishmentDetailResponse = z.infer<
  typeof GetReplenishmentDetailResponseSchema
>;

export const replenishmentDetailContracts = {
  get: oc
    .input(GetReplenishmentDetailInputSchema)
    .output(GetReplenishmentDetailResponseSchema)
    .route({
      method: "GET",
      summary:
        "One SKUL's available replenishment inputs, display buckets, saved plans and PIPE provenance.",
      description:
        "Read a SKU's stock trajectory, active replenishment plans, and incoming supply at one warehouse.",
    }),
  getSupplyDemandLines: getSupplyDemandLinesContract,
} as const;
